import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk';
import { datadogLogs } from '@datadog/browser-logs';
import Button from '@fff-web/fff-ui-shared/Button';
import { useUserContext } from '@fff-web/fff-utilities';
import { HCaptchaContainer } from '@fff-web/fff-payments';
import { identify } from '@fff-web/fff-analytics';
import {
  useReactivateMyMembershipContext,
  type IReactivateMembershipContext,
} from '../../utils/ContextProviders/ReactivateContext';
import useGetPricesByPlan from '../../utils/hooks/useGetPricesByPlan';
import Layout from '../../components/Layout';
import TermsAndConditions from './TermsAndConditions';
import HeroBannerContainer from './HeroBannerContainer';
import HowItWorks from './HowItWorks';
import MembershipPlans from './MembershipPlans';
import { PriceSummary } from './PriceSummary';
import env from '../MyAccount/utils/env';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import { reactivateExpiredSubscription } from '../../services/subscription';
import { sendReactivateSegmentEventAfterResponse, sendGTMReactivateConversion } from './Events';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import AllErrorMessage from '../../components/AllErrorMessage';
import { ReactivationMessages } from './ReactivateUtils';
import { executeCaptcha } from '../../utils/functions/executeCaptcha';
import { useReactivatePlanDataContext } from '../../utils/ContextProviders/ReactivatePlanDataContext';
import { PromotionBannerContainer } from '../../components/PromotionBannerContainer';
import { UpdatePaymentModalContainer } from './UpdatePaymentModalContainer/';
import styles from './ReactivateMembership.module.scss';
import { ReactivateCarousel } from './ReactivateCarousel';

const PAGE_TITLE = 'Reactivate Membership';

const ReactivateMembership = () => {
  const history = useHistory();
  const { growthMyMembership } = useReactivateMyMembershipContext();
  const { userInfo } = useUserContext();
  const { isLoadingPrices } = useGetPricesByPlan();
  const { variables } = growthMyMembership as IReactivateMembershipContext;
  const planSectionRef = useRef<HTMLDivElement>(null);
  const captchaRef = useRef(null);
  const [submitPactSafeContract, setSubmitPactSafeContract] = useState(false);
  const { howItWorks } = variables;
  const thanksPage = '/reactivate-thanks';

  const scrollToPlanSection = () => {
    const section = planSectionRef.current;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const { couponData, selectedPlanCode, promoCodeType } = useReactivatePlanDataContext();

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(history, thanksPage, couponData?.code);

  const handleSubmit = async () => {
    try {
      const captchaToken = await executeCaptcha(captchaRef);
      memoizedIsCheckboxChecked();
      setLoading(true);

      const response = await reactivateExpiredSubscription(
        selectedPlanCode,
        couponData?.code,
        captchaToken,
        promoCodeType,
      );

      const status = getPlanChangeCompletedEventStatusFromResponse(response);
      const eventsToFire = [
        sendReactivateSegmentEventAfterResponse(status, ReactivationMessages.SUBMIT_CTA_NAME),
      ];

      if (status === 'success') {
        eventsToFire.push(identify({ traits: { planCode: selectedPlanCode } }));
        sendGTMReactivateConversion(response);
      }

      await Promise.allSettled(eventsToFire);
      handlePlanChangeResponse(response);
    } catch (error) {
      datadogLogs.logger.error(ReactivationMessages.SUBSCRIPTION_ERROR, {}, error);
      setLoading(false);
    }
  };

  const clickWrapConfig = {
    accessId: env.pactSafeAccessId,
    groupKey: env.pactSafeGroupKey,
    groupId: env.pactSafeGroupId,
    signerId: userInfo?.user.email,
    testMode: env.appEnv === 'dev',
    snapshotLocation: env.pactSafeSnapshotId,
  };

  const isCheckboxChecked = useCallback(() => {
    const checkboxElement = document.getElementById(
      `ps-contract-checkbox-group-${clickWrapConfig?.groupId}`,
    ) as HTMLInputElement;
    if (clickWrapConfig?.signerId && checkboxElement && !checkboxElement.checked) {
      setTimeout(() => {
        checkboxElement.click();
        checkboxElement.disabled = true;
        setSubmitPactSafeContract(true);
      }, 1000);
    }
    return checkboxElement;
  }, [clickWrapConfig?.groupId, clickWrapConfig?.signerId]);

  const memoizedIsCheckboxChecked = useMemo(() => isCheckboxChecked, [isCheckboxChecked]);

  return (
    <>
      <PromotionBannerContainer onClick={scrollToPlanSection} />
      <Layout
        isLoading={isLoadingPrices || isLoading}
        pageTitle={PAGE_TITLE}
        redirectIfNoAuth
        showError={showRetry}
        ErrorComponent={() => (
          <AllErrorMessage subjectAction="reactivate" onButtonClick={onClose} />
        )}
        hideChatWidget={showPayment}
      >
        <main className={styles.reactivateMainWrapper}>
          <HeroBannerContainer onCTAClick={scrollToPlanSection} />
          <HowItWorks headline={howItWorks?.headline} productsList={howItWorks?.productsList} />
          <ReactivateCarousel cardClickHandler={scrollToPlanSection} />
          <MembershipPlans planSectionRef={planSectionRef} />
          <section className={styles.summarySectionWrapper}>
            <PriceSummary />
            <TermsAndConditions />
          </section>
          <section className={styles.ctaSectionWrapper}>
            <Button disabled={isLoading} onClick={handleSubmit}>
              {ReactivationMessages.SUBMIT_CTA_NAME}
            </Button>
          </section>
          <UpdatePaymentModalContainer
            onClose={onClose}
            subtitle={errorPaymentMessage}
            isOpen={showPayment}
            onSuccess={handleSubmit}
            hasError={!!errorPaymentMessage}
          />
          <HCaptchaContainer ref={captchaRef} />
          <div className={styles.pactsafeWrapper}>
            <PSClickWrap
              signerId={clickWrapConfig.signerId}
              accessId={clickWrapConfig.accessId}
              groupKey={clickWrapConfig.groupKey}
              snapshotLocation={clickWrapConfig.snapshotLocation}
              disableSending={submitPactSafeContract}
              displayImmediately={false}
              testMode={clickWrapConfig.testMode}
              clickWrapStyle="combined"
            />
          </div>
        </main>
      </Layout>
    </>
  );
};

export default ReactivateMembership;
